import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Avatar,
  Tooltip,
  Modal,
  Upload,
  Progress,
  Tag,
} from "antd";
import {
  FaSearch,
  FaUsers,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaFileExcel,
  FaUpload,
  FaDownload,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import axios from "axios";
import Api from "../api/Api";

const { Content } = Layout;
const { Title, Text } = Typography;

/* ──────────────────── HELPERS ──────────────────── */

/**
 * Format: 1 000,00 / 1 000 000,00 / 500,00
 * Uses pt-AO locale which outputs "AOA 1 000,00" — we strip the currency symbol
 * and keep only the number part.
 */
const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return new Intl.NumberFormat("pt-AO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/* ──────────────────── EXPORT EXCEL ──────────────────── */
const exportToExcel = (users) => {
  const data = users.map((u) => ({
    employee_id:  u.employee_id,
    nome:         u.nome,
    funcao:       u.funcao,
    salario:      parseFloat(u.salario)      || 0,
    salario_hora: parseFloat(u.salario_hora) || 0,
  }));

  const ws = XLSX.utils.json_to_sheet(data, {
    header: ["employee_id", "nome", "funcao", "salario", "salario_hora"],
  });

  /* Custom header labels */
  ws["A1"] = { v: "employee_id",    t: "s" };
  ws["B1"] = { v: "nome",     t: "s" };
  ws["C1"] = { v: "funcao",            t: "s" };
  ws["D1"] = { v: "salario",t: "s" };
  ws["E1"] = { v: "salario_hora",t: "s" };

  /* Column widths */
  ws["!cols"] = [
    { wch: 16 },
    { wch: 34 },
    { wch: 30 },
    { wch: 22 },
    { wch: 22 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Utilizadores");
  XLSX.writeFile(wb, "utilizadores_modelo.xlsx");
};

/* ──────────────────── IMPORT MODAL ──────────────────── */
const ImportModal = ({ open, onClose, onSuccess }) => {
  const [step,     setStep]     = useState("upload"); // upload | preview | sending
  const [rows,     setRows]     = useState([]);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const reset = () => {
    setStep("upload");
    setRows([]);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => { reset(); onClose(); };

  /* Parse the uploaded xlsx */
  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb  = XLSX.read(e.target.result, { type: "array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });

        // Normalise keys: accept both header labels and field names
        const normalised = raw.map((row) => ({
          employee_id:  String(row["ID Colaborador"]    ?? row["employee_id"]  ?? "").trim(),
          nome:         String(row["Nome Completo"]     ?? row["nome"]         ?? "").trim(),
          funcao:       String(row["Função"]            ?? row["funcao"]       ?? "").trim(),
          salario:      parseFloat(row["Salário Base (AOA)"] ?? row["salario"]      ?? 0) || 0,
          salario_hora: parseFloat(row["Salário Hora (AOA)"] ?? row["salario_hora"] ?? 0) || 0,
        }));

        const valid = normalised.filter((r) => r.employee_id);
        if (!valid.length) {
          toast.error("Nenhuma linha válida encontrada. Verifique o ficheiro.");
          return;
        }
        setRows(valid);
        setStep("preview");
      } catch {
        toast.error("Erro ao ler o ficheiro. Certifique-se que é um .xlsx válido.");
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent antd auto-upload
  };

  /* Send to backend */
  const handleSend = async () => {
    setStep("sending");
    setProgress(0);

    const data = rows.map((r) => ({
      employee_id:  r.employee_id,
      nome:         r.nome,
      funcao:       r.funcao,
      salario:      r.salario,
      salario_hora: r.salario_hora,
    }));

    try {
      // Simulate progress while request is in flight
      const timer = setInterval(() => {
        setProgress((p) => (p < 85 ? p + 5 : p));
      }, 200);

await axios.post(
  "https://api-crm2.technext.ao/api/importUpdateUsers",
  {
    data: data, // explícito
  },
  {
    headers: {
      "Content-Type": "application/json",
    },
  }
);

      clearInterval(timer);
      setProgress(100);
      toast.success(`${rows.length} registo(s) importado(s) com sucesso!`);
      onSuccess();
      setTimeout(handleClose, 800);
    } catch (err) {
      setStep("preview");
      setProgress(0);
      toast.error("Erro ao importar. Tente novamente.");
    }
  };

  const previewColumns = [
    { title: "ID",           dataIndex: "employee_id",  width: 110 },
    { title: "Nome",         dataIndex: "nome",         ellipsis: true },
    { title: "Função",       dataIndex: "funcao",       ellipsis: true },
    {
      title: "Salário Base",
      dataIndex: "salario",
      align: "right",
      width: 140,
      render: (v) => <Text strong style={{ color: "#1677ff" }}>{formatNumber(v)}</Text>,
    },
    {
      title: "Salário Hora",
      dataIndex: "salario_hora",
      align: "right",
      width: 140,
      render: (v) => <Text>{formatNumber(v)}</Text>,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={780}
      title={
        <Space>
          <FaFileExcel style={{ color: "#52c41a", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Importar Excel</span>
        </Space>
      }
      footer={
        step === "preview"
          ? [
              <Button key="back" onClick={reset} style={{ borderRadius: 6 }}>
                Voltar
              </Button>,
              <Button
                key="send"
                type="primary"
                icon={<FaUpload />}
                onClick={handleSend}
                style={{ borderRadius: 6, fontWeight: 500 }}
              >
                Importar {rows.length} registo(s)
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={handleClose} style={{ borderRadius: 6 }}>
                Cancelar
              </Button>,
            ]
      }
      destroyOnClose
    >
      {step === "upload" && (
        <Upload.Dragger
          accept=".xlsx,.xls"
          beforeUpload={handleFile}
          showUploadList={false}
          style={{ borderRadius: 12 }}
        >
          <div style={{ padding: "24px 0" }}>
            <FaFileExcel style={{ fontSize: 48, color: "#52c41a", marginBottom: 12 }} />
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
              Arraste o ficheiro ou clique para selecionar
            </p>
            <p style={{ color: "#8c8c8c", fontSize: 13 }}>
              Suporta ficheiros <strong>.xlsx</strong> gerados pelo botão "Exportar Modelo"
            </p>
          </div>
        </Upload.Dragger>
      )}

      {step === "preview" && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 16,
              padding: "10px 14px",
              backgroundColor: "#f6ffed",
              borderRadius: 8,
              border: "1px solid #b7eb8f",
            }}
          >
            <FaCheckCircle style={{ color: "#52c41a" }} />
            <Text style={{ color: "#389e0d" }}>
              <strong>{rows.length}</strong> registo(s) encontrado(s). Revise antes de importar.
            </Text>
          </div>
          <Table
            columns={previewColumns}
            dataSource={rows}
            rowKey="employee_id"
            size="small"
            pagination={{ pageSize: 8, size: "small" }}
            scroll={{ x: 600 }}
          />
        </>
      )}

      {step === "sending" && (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <FaUpload style={{ fontSize: 40, color: "#1677ff", marginBottom: 16 }} />
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
            A importar {rows.length} registo(s)…
          </p>
          <Progress percent={progress} strokeColor="#1677ff" style={{ maxWidth: 400 }} />
        </div>
      )}
    </Modal>
  );
};

/* ──────────────────── PÁGINA PRINCIPAL ──────────────────── */
const SalariosPermissoes = () => {
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [importOpen,  setImportOpen]  = useState(false);

  useEffect(() => {
    document.title = "Salários e Permissões | Dashboard";
    loadUsers();
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListUser();
      setUsers(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar utilizadores.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      !q ||
      u.nome?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.funcao?.toLowerCase().includes(q)
    );
  });

  const totalUsers           = users.length;
  const usersWithLogistica   = users.filter((u) => u.aprovar_sala === "S").length;
  const usersWithoutLogistica = totalUsers - usersWithLogistica;

  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      width: 240,
      render: (nome, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar size="large" style={{ backgroundColor: "#1677ff" }}>
            {nome?.[0]}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f" }}>{nome}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.funcao}</Text>
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 240,
      ellipsis: true,
    },
    {
      title: "Salário Base",
      dataIndex: "salario",
      key: "salario",
      width: 160,
      align: "right",
      render: (val) => (
        <Text strong style={{ color: "#1677ff" }}>
          {formatNumber(val)}
        </Text>
      ),
    },
    {
      title: "Salário Hora",
      dataIndex: "salario_hora",
      key: "salario_hora",
      width: 160,
      align: "right",
      render: (val) => (
        <Text>{val && parseFloat(val) > 0 ? `${formatNumber(val)}/h` : "—"}</Text>
      ),
    },
  ];

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaMoneyBillWave style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Salários e Permissões
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Configure os salários e acessos à logística de veículos
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Space size={12}>
                {/* Exportar modelo */}
                <Tooltip title="Exportar Excel com dados actuais para editar em massa">
                  <Button
                    icon={<FaDownload />}
                    onClick={() => exportToExcel(users)}
                    disabled={!users.length}
                    style={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.4)",
                      fontWeight: 600,
                      borderRadius: 30,
                      padding: "0 20px",
                      height: 44,
                    }}
                  >
                    Exportar Modelo
                  </Button>
                </Tooltip>

                {/* Importar Excel */}
                <Button
                  icon={<FaUpload />}
                  onClick={() => setImportOpen(true)}
                  style={{
                    backgroundColor: "#fff",
                    color: "#1677ff",
                    border: "none",
                    fontWeight: 600,
                    borderRadius: 30,
                    padding: "0 24px",
                    height: 44,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  Importar Excel
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          {[
            {
              title: "Total de Utilizadores",
              value: totalUsers,
              color: "#1677ff",
              bg: "#e6f4ff",
              icon: <FaUsers style={{ fontSize: 24, color: "#1677ff" }} />,
            },
            {
              title: "Com Permissão Logística",
              value: usersWithLogistica,
              color: "#52c41a",
              bg: "#f6ffed",
              icon: <FaCheckCircle style={{ fontSize: 24, color: "#52c41a" }} />,
            },
            {
              title: "Sem Permissão Logística",
              value: usersWithoutLogistica,
              color: "#ff4d4f",
              bg: "#fff1f0",
              icon: <FaTimesCircle style={{ fontSize: 24, color: "#ff4d4f" }} />,
            },
          ].map((stat) => (
            <Col xs={24} sm={8} key={stat.title}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Statistic
                  title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>{stat.title}</span>}
                  value={stat.value}
                  valueStyle={{ fontSize: 32, fontWeight: 700, color: stat.color }}
                  prefix={
                    <div
                      style={{
                        backgroundColor: stat.bg,
                        borderRadius: 12,
                        padding: 10,
                        marginRight: 16,
                      }}
                    >
                      {stat.icon}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barra de pesquisa */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Input
            size="large"
            prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
            placeholder="Pesquisar por nome, email ou função..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ borderRadius: 30, maxWidth: 500 }}
          />
        </Card>

        {/* Tabela */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} utilizadores`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhum utilizador encontrado" }}
            scroll={{ x: 900 }}
          />
        </Card>

        {/* Modal importação */}
        <ImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onSuccess={() => { loadUsers(); setImportOpen(false); }}
        />
      </Content>
    </Layout>
  );
};

export default SalariosPermissoes;